import React, { useEffect, useState } from "react";
import { genericGetAll } from "../generic/crud";
import { ENTITY_ID_WATER_WASH } from "../generic/Constants";
import { Pagination } from "react-bootstrap";
import { convertDateToRelativeTime } from "../../utils/Utils";

export function WaterWashList({ branchId, refreshCounter }) {
    const [records, setRecords] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [sizePerPage] = useState(20);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [page, branchId, refreshCounter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: page,
                sizePerPage: sizePerPage,
                currentIndex: (page - 1) * sizePerPage,
                sortBy: 'createdOn',
                sortOrder: 'desc'
            };

            if (branchId) {
                params.fixedFilterColumn = 'branchId';
                params.fixedFilterData = branchId;
            }

            const response = await genericGetAll(ENTITY_ID_WATER_WASH, params);
            if (response.data) {
                setRecords(response.data.entities || []);
                setTotalCount(response.data.totalCount || 0);
            }
        } catch (error) {
            console.error("Error fetching water wash records:", error);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderPagination = () => {
        if (totalCount <= sizePerPage) return null;

        const totalPages = Math.ceil(totalCount / sizePerPage);
        let items = [];
        // Simple pagination for now: First, Prev, Current, Next, Last if needed, 
        // but here just a standard list of numbers restricted to a window could be better if many pages.
        // For simplicity, showing all or a small window.
        
        for (let number = 1; number <= totalPages; number++) {
            // Show only first, last, current, and surrounding
            if (number === 1 || number === totalPages || (number >= page - 2 && number <= page + 2)) {
                 items.push(
                    <Pagination.Item key={number} active={number === page} onClick={() => setPage(number)}>
                        {number}
                    </Pagination.Item>,
                );
            } else if (number === page - 3 || number === page + 3) {
                 items.push(<Pagination.Ellipsis key={number} />);
            }
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>{items}</Pagination>
            </div>
        );
    };

    return (
        <div className="card card-flush">
            <div className="card-body pt-0">
                {loading ? (
                    <div className="text-center py-10">
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : records.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                            <thead>
                                <tr className="fw-bold text-muted text-uppercase fs-7">
                                    <th className="min-w-50px">#</th>
                                    <th className="min-w-150px">Vehicle Details</th>
                                    <th className="min-w-150px">Job Info</th>
                                    <th className="min-w-150px">Personnel</th>
                                    <th className="min-w-150px">Wash Date</th>
                                    {!branchId && <th className="min-w-150px">Branch</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((record, index) => (
                                    <tr key={record.id}>
                                        <td>
                                            <span className="text-muted fw-semibold d-block fs-7">
                                                {(page - 1) * sizePerPage + index + 1}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">
                                                    {record.vehicleNumber}
                                                </span>
                                                <span className="text-muted fw-semibold d-block fs-7">
                                                    {record.vehicleModelName || "N/A"}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge badge-light-info fw-bold">
                                                {record.jobType?.replace('_', ' ') || "N/A"}
                                            </span>
                                            {record.isRepeatWash && (
                                                <span className="badge badge-light-warning fw-bold ms-2">
                                                    Repeat Wash
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="text-dark fw-bold d-block fs-6">
                                                {record.advisorName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="text-dark fw-bold d-block fs-6">
                                                    {convertDateToRelativeTime(record.createdOn)}
                                                </span>
                                                <span className="text-muted fw-semibold d-block fs-7">
                                                    {formatDate(record.createdOn)}
                                                </span>
                                            </div>
                                        </td>
                                        {!branchId && (
                                            <td>
                                                <span className="text-muted fw-semibold d-block fs-7">
                                                    {record.branchName || "N/A"}
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {renderPagination()}
                    </div>
                ) : (
                    <div className="text-center py-10">
                        <i className="bi bi-droplet-half text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="text-muted mt-3">No water wash records found.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
